import React, { useState, useEffect, useCallback } from 'react';
import { Container, Card, Row, Col } from 'react-bootstrap';
import { get } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';
import moment from 'moment';

import Loading from '../../components/Loading';
import axios from '../../services/axios';
import Breadcrumbs from '../../components/Breadcrumbs';
import { useNotification } from '../../components/Notification/NotificationContext';
import Forms from '../../components/Forms';
import ErrorProcessor from '../../components/ErrorProcessor';
import PageHeader from '../../components/PageHeader';

export default function User() {
  const routeName = '/companies';
  const titleName = 'Empresas';
  const dataName = 'Empresa';
  const [dataLoaded, setDataLoaded] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const notification = useNotification();
  const navigate = useNavigate();
  const params = useParams();
  const id = get(params, 'id', '');
  const [formValues, setFormValues] = useState({
    name: '',
  });

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, 'Nome deve conter no mínimo 3 caracteres.')
      .required('Nome obrigatório.'),
    cnpj: Yup.number()
      .typeError('Deve ser numérico')
      .min(14, 'CNPJ inválido.')
      .nullable(),
    email: Yup.string().email('E-mail inválido.').nullable(),
    business_phone: Yup.number()
      .typeError('Deve ser numérico')
      .test(
        'len',
        'Número telefônico inválido, formato correto DDD + Número',
        (value) => !value || value.toString().length >= 10
      )
      .nullable(),
    mobile_phone: Yup.number()
      .typeError('Deve ser numérico')
      .test(
        'len',
        'Número telefônico inválido, formato correto DDD + Número',
        (value) => !value || value.toString().length >= 10
      )
      .nullable(),
    postal_code: Yup.number()
      .typeError('Deve ser numérico')
      .min(7, 'CEP deve conter no mínimo 7 dígitos.')
      .nullable(),
    street: Yup.string().nullable(),
    number: Yup.number().typeError('Deve ser numérico').nullable(),
    complement: Yup.string().nullable(),
    city: Yup.string().nullable(),
    neighborhood: Yup.string().nullable(),
    state: Yup.string().nullable(),
    founding_date: Yup.date().typeError('Deve ser uma data válida').nullable(),
  });

  const handleFieldChange = useCallback((fieldName, value) => {
    let newValue = false;
    if (fieldName === 'founding_date') {
      let validateDate = moment(value, 'DD/MM/YYYY', true);

      if (!validateDate.isValid()) {
        validateDate = moment(value, 'YYYY-MM-DD', true);
      }

      newValue = validateDate.isValid()
        ? validateDate.format('YYYY-MM-DD 12:00:00')
        : null;
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      [fieldName]: newValue === false ? value : newValue,
    }));
  }, []);

  const fields = [
    {
      name: 'name',
      id: 'name',
      label: 'Nome da empresa',
      type: 'text',
      onChange: (e) => handleFieldChange('name', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Pessoa jurídica',
    },
    {
      name: 'cnpj',
      id: 'cnpj',
      label: 'CNPJ',
      type: 'number',
      onChange: (e) => handleFieldChange('cnpj', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Pessoa jurídica',
    },
    {
      name: 'founding_date',
      id: 'founding_date',
      label: 'Data de fundação',
      type: 'date',
      onChange: (e) => handleFieldChange('founding_date', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Pessoa jurídica',
    },
    {
      name: 'email',
      id: 'email',
      label: 'E-mail',
      type: 'email',
      onChange: (e) => handleFieldChange('email', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Contato',
    },
    {
      name: 'business_phone',
      id: 'business_phone',
      label: 'Telefone comercial',
      type: 'number',
      onChange: (e) => handleFieldChange('business_phone', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Contato',
    },
    {
      name: 'mobile_phone',
      id: 'mobile_phone',
      label: 'Celular',
      type: 'number',
      onChange: (e) => handleFieldChange('mobile_phone', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Contato',
    },
    {
      name: 'postal_code',
      id: 'postal_code',
      label: 'CEP',
      type: 'number',
      onChange: (e) => handleFieldChange('postal_code', e.target.value),
      colSize: 3,
      additionalProps: {},
      group: 'Endereço',
    },
    {
      name: 'street',
      id: 'street',
      label: 'Logradouro',
      type: 'text',
      onChange: (e) => handleFieldChange('street', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Endereço',
    },
    {
      name: 'number',
      id: 'number',
      label: 'Número',
      type: 'number',
      onChange: (e) => handleFieldChange('number', e.target.value),
      colSize: 2,
      additionalProps: {},
      group: 'Endereço',
    },
    {
      name: 'complement',
      id: 'complement',
      label: 'Complemento',
      type: 'text',
      onChange: (e) => handleFieldChange('complement', e.target.value),
      colSize: 3,
      additionalProps: {},
      group: 'Endereço',
    },
    {
      name: 'neighborhood',
      id: 'neighborhood',
      label: 'Bairro',
      type: 'text',
      onChange: (e) => handleFieldChange('neighborhood', e.target.value),
      colSize: 4,
      additionalProps: {},
      group: 'Endereço',
    },
    {
      name: 'city',
      id: 'city',
      label: 'Cidade / Município',
      type: 'text',
      onChange: (e) => handleFieldChange('city', e.target.value),
      colSize: 3,
      additionalProps: {},
      group: 'Endereço',
    },
    {
      name: 'state',
      id: 'state',
      label: 'UF',
      type: 'text',
      onChange: (e) => handleFieldChange('state', e.target.value),
      colSize: 2,
      additionalProps: {},
      group: 'Endereço',
    },
  ];

  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      let action = 'cadastrado';

      if (id) {
        await axios.put(`${routeName}/${id}`, formValues);
        action = 'atualizada';
      } else {
        await axios.post(routeName, formValues);
      }
      navigate(routeName);
      notification(`${dataName} ${action} com sucesso`, 'success');
    } catch (err) {
      setApiErrors(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    async function getData() {
      try {
        setIsLoading(true);
        const { data } = await axios.get(`${routeName}/${id}`);
        if (data.founding_date) {
          data.founding_date = moment
            .utc(data.founding_date)
            .format('YYYY-MM-DD');
        }
        setFormValues({
          ...data,
        });
      } catch (err) {
        setApiErrors(err);
      } finally {
        setIsLoading(false);
        setDataLoaded(true);
      }
    }

    getData();
  }, [id]);

  return (
    <Container fluid className="px-lg-4 px-xl-5">
      <Loading isLoading={isLoading} />
      <Breadcrumbs
        title={id ? 'Atualizar' : 'Cadastrar'}
        pages={[{ link: routeName, name: 'Empresas' }]}
      />
      <PageHeader title={titleName} />
      <section>
        <Row>
          <Col lg={12}>
            <Card className="mb-5">
              <Card.Header>
                <h4 className="card-heading">
                  {id ? 'Atualizar' : 'Cadastrar'}
                </h4>
              </Card.Header>
              <Card.Body>
                <ErrorProcessor error={apiErrors} />
                {!id || dataLoaded ? (
                  <Forms
                    initialValues={formValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    fields={fields}
                    btnSubmit={{
                      variant: 'primary',
                      type: 'submit',
                      text: id ? 'Atualizar' : 'Cadastrar',
                    }}
                  />
                ) : null}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </section>
    </Container>
  );
}
